import { FC } from 'react';
import { Badge, Tooltip, theme as antdTheme } from 'antd';
import styled from 'styled-components';
import StateIcon, { JobStatusType } from 'packages/lib/common-ui/state-icon';
import { red } from '@mui/material/colors';
import { useRecoilValue } from 'recoil';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';
import { CopyOutlined } from '@ant-design/icons';
import { onClickCopy } from 'packages/lib/utils/on-click-copy';

const { useToken } = antdTheme;

type ErrorInfo = {
  id: string;
  message: string;
  copyText: string;
};

interface ConsolidatedErrorIconProps {
  errors: ErrorInfo[];
}

const ErrorIconContainer = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  font-size: 8px;
  margin-left: 5px;
  margin-bottom: 2px;
  color: white;
  background-color: rgba(0, 0, 0, 0.45);
  border-radius: 5px;
  z-index: 6;
  cursor: pointer;
`;

const ErrorList = styled.ol`
  list-style-position: inside;
  padding-left: 0;
  margin: 0;
`;

const ErrorListItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
  gap: 1em;
`;

const CopyIcon = styled(CopyOutlined)`
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

export const ConsolidatedErrorIcon: FC<ConsolidatedErrorIconProps> = ({
  errors,
}) => {
  const appTheme = useRecoilValue(uiThemeAtom);
  const { token } = useToken();

  if (errors.length === 0) {
    return null;
  }

  const popoverBackgroundColor =
    appTheme === 'dark' ? token.colorBgElevated : 'rgba(0, 0, 0, 0.85)';

  const handleCopy = async (
    e: React.MouseEvent<HTMLElement>,
    textToCopy: string
  ) => {
    e.stopPropagation(); // Prevent the tooltip from closing when the copy icon is clicked
    await onClickCopy(textToCopy, 'Error details');
  };

  const errorContent = (
    <ErrorList>
      {errors.map((error) => (
        <ErrorListItem key={error.id}>
          <span>{error.message}</span>
          <CopyIcon onClick={(e) => handleCopy(e, error.copyText)} />
        </ErrorListItem>
      ))}
    </ErrorList>
  );

  return (
    <>
      <Tooltip
        title={errorContent}
        trigger="hover"
        placement="bottomLeft"
        color={popoverBackgroundColor}
      >
        <ErrorIconContainer>
          <Badge
            count={errors.length > 1 ? errors.length : 0}
            size="small"
            style={{ backgroundColor: red[500], boxShadow: 'none' }}
          >
            <StateIcon state={JobStatusType.Error} />
          </Badge>
        </ErrorIconContainer>
      </Tooltip>
    </>
  );
};
